import { doc, getDoc } from 'firebase/firestore'
import { ENDPOINTS } from '@/config/endpoints'
import { db } from '@/lib/firebase'

const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
} as const

const PAIRWISE_SESSION_KEY = 'pairwise-matcher-session-id'
const PAIRWISE_USER_KEY = 'pairwise-matcher-user-id'

export interface PairwiseChooseRequest {
  userId: string
  sessionId: string | null
  chosenId: string | null
  totalRounds: number
  currentOptionAId: string | null
  currentOptionBId: string | null
  ownerUserId?: string | null
  selectedCity?: string | null
  intentMode?: 'specific' | 'exploring' | null
  ideaText?: string | null
}

export interface PairwiseChooseResponse {
  sessionId: string
  userId: string
  stepIndex: number
  round: number
  totalRounds: number
  done: boolean
  currentPair: {
    optionAId: string
    optionBId: string
  } | null
  nextByChosenId: Array<{
    chosenId: string
    nextPair: {
      optionAId: string
      optionBId: string
    }
  }>
}

export interface PairwiseResultResponse {
  sessionId: string
  topMatches: Array<{
    artistId: string
    score: number
  }>
}

export interface MatcherOption {
  id: string
  imageUrl: string
  caption?: string
  artistId?: string
}

export type MatcherSessionStatus = 'in_progress' | 'completed'
export type MatcherSessionFlowStep = 'pairwise' | 'preferences' | 'results'

export interface StoredMatcherResultMatch {
  artistId: string
  score: number
  rank: number
}

export interface StoredMatcherSession {
  sessionId: string
  ownerUserId: string | null
  matcherUserId: string
  status: MatcherSessionStatus
  flowStep: MatcherSessionFlowStep
  stepIndex: number
  currentRound: number
  totalRounds: number
  isDone: boolean
  currentPair: PairwiseChooseResponse['currentPair']
  nextByChosenId: PairwiseChooseResponse['nextByChosenId']
  selectedCity: string
  intentMode: 'specific' | 'exploring'
  ideaText: string
  likedItemIds: string[]
  dislikedItemIds: string[]
  skippedItemIds: string[]
  resultSnapshot: StoredMatcherResultMatch[]
  createdAt: Date | null
  updatedAt: Date | null
  completedAt: Date | null
}

function toDateOrNull(value: unknown): Date | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  if (value && typeof value === 'object' && 'seconds' in value && typeof (value as { seconds: unknown }).seconds === 'number') {
    return new Date((value as { seconds: number }).seconds * 1000)
  }

  return null
}

function normalizeStoredMatcherSession(data: Record<string, unknown>): StoredMatcherSession {
  return {
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : '',
    ownerUserId: typeof data.ownerUserId === 'string' ? data.ownerUserId : null,
    matcherUserId: typeof data.matcherUserId === 'string'
      ? data.matcherUserId
      : (typeof data.userId === 'string' ? data.userId : ''),
    status: data.status === 'completed' ? 'completed' : 'in_progress',
    flowStep: data.flowStep === 'results' || data.flowStep === 'preferences' ? data.flowStep : 'pairwise',
    stepIndex: typeof data.stepIndex === 'number' ? data.stepIndex : 0,
    currentRound: typeof data.currentRound === 'number' ? data.currentRound : (typeof data.stepIndex === 'number' ? data.stepIndex + 1 : 1),
    totalRounds: typeof data.totalRounds === 'number' ? data.totalRounds : 20,
    isDone: typeof data.isDone === 'boolean' ? data.isDone : data.status === 'completed',
    currentPair: data.currentPair && typeof data.currentPair === 'object'
      ? (data.currentPair as PairwiseChooseResponse['currentPair'])
      : null,
    nextByChosenId: Array.isArray(data.nextByChosenId)
      ? data.nextByChosenId
          .map((entry) => {
            if (!entry || typeof entry !== 'object') {
              return null
            }

            const choice = entry as Record<string, unknown>
            const nextPair = choice.nextPair
            if (
              typeof choice.chosenId !== 'string' ||
              !nextPair ||
              typeof nextPair !== 'object' ||
              typeof (nextPair as Record<string, unknown>).optionAId !== 'string' ||
              typeof (nextPair as Record<string, unknown>).optionBId !== 'string'
            ) {
              return null
            }

            return {
              chosenId: choice.chosenId,
              nextPair: {
                optionAId: (nextPair as Record<string, unknown>).optionAId as string,
                optionBId: (nextPair as Record<string, unknown>).optionBId as string,
              },
            }
          })
          .filter((entry): entry is PairwiseChooseResponse['nextByChosenId'][number] => entry !== null)
      : [],
    selectedCity: typeof data.selectedCity === 'string' ? data.selectedCity : '',
    intentMode: data.intentMode === 'specific' ? 'specific' : 'exploring',
    ideaText: typeof data.ideaText === 'string' ? data.ideaText : '',
    likedItemIds: Array.isArray(data.likedItemIds) ? data.likedItemIds.filter((value): value is string => typeof value === 'string') : [],
    dislikedItemIds: Array.isArray(data.dislikedItemIds) ? data.dislikedItemIds.filter((value): value is string => typeof value === 'string') : [],
    skippedItemIds: Array.isArray(data.skippedItemIds) ? data.skippedItemIds.filter((value): value is string => typeof value === 'string') : [],
    resultSnapshot: Array.isArray(data.resultSnapshot)
      ? data.resultSnapshot
          .map((entry) => {
            if (!entry || typeof entry !== 'object') {
              return null
            }

            const match = entry as Record<string, unknown>
            if (
              typeof match.artistId !== 'string' ||
              typeof match.score !== 'number' ||
              typeof match.rank !== 'number'
            ) {
              return null
            }

            return {
              artistId: match.artistId,
              score: match.score,
              rank: match.rank,
            } satisfies StoredMatcherResultMatch
          })
          .filter((entry): entry is StoredMatcherResultMatch => entry !== null)
      : [],
    createdAt: toDateOrNull(data.createdAt),
    updatedAt: toDateOrNull(data.updatedAt),
    completedAt: toDateOrNull(data.completedAt),
  }
}

export async function choosePairwiseOption(payload: PairwiseChooseRequest): Promise<PairwiseChooseResponse> {
  const response = await fetch(ENDPOINTS.pairwise_matcher_choose, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(`Pairwise choose failed (${response.status})`)
  }

  return response.json() as Promise<PairwiseChooseResponse>
}

export async function fetchPairwiseResults(payload: {
  sessionId: string
  userId: string | null
  ownerUserId?: string | null
  city?: string
  intentMode?: 'specific' | 'exploring' | null
  ideaText?: string | null
  topK?: number
  recompute?: boolean
}): Promise<PairwiseResultResponse> {
  const response = await fetch(ENDPOINTS.pairwise_matcher_result, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      sessionId: payload.sessionId,
      userId: payload.userId,
      ownerUserId: payload.ownerUserId ?? null,
      city: payload.city,
      intentMode: payload.intentMode ?? null,
      ideaText: payload.ideaText ?? null,
      topK: payload.topK ?? 5,
      recompute: payload.recompute ?? false,
    }),
  })

  if (!response.ok) {
    throw new Error(`Pairwise result failed (${response.status})`)
  }

  return response.json() as Promise<PairwiseResultResponse>
}

export async function listStoredMatcherSessions(ownerUserId: string): Promise<StoredMatcherSession[]> {
  const response = await fetch(`${ENDPOINTS.pairwise_matcher_sessions}?ownerUserId=${encodeURIComponent(ownerUserId)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Pairwise sessions failed (${response.status})`)
  }

  const payload = await response.json() as { sessions?: unknown[] }
  return Array.isArray(payload.sessions)
    ? payload.sessions
        .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
        .map((entry) => normalizeStoredMatcherSession(entry))
    : []
}

export async function deleteMatcherSession(payload: {
  sessionId: string
  ownerUserId: string
}): Promise<void> {
  const response = await fetch(ENDPOINTS.pairwise_matcher_session_delete, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      sessionId: payload.sessionId,
      ownerUserId: payload.ownerUserId,
    }),
  })

  if (!response.ok) {
    throw new Error(`Delete session failed (${response.status})`)
  }
}

export async function saveMatcherResultsSession(payload: {
  sessionId: string
  ownerUserId: string
  selectedCity?: string
  intentMode?: 'specific' | 'exploring' | null
  ideaText?: string | null
}): Promise<void> {
  const response = await fetch(ENDPOINTS.pairwise_matcher_save_results, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      sessionId: payload.sessionId,
      ownerUserId: payload.ownerUserId,
      selectedCity: payload.selectedCity ?? '',
      intentMode: payload.intentMode ?? null,
      ideaText: payload.ideaText ?? null,
    }),
  })

  if (!response.ok) {
    throw new Error(`Pairwise save results failed (${response.status})`)
  }
}

function getItemCandidates(optionId: string): string[] {
  const candidates = new Set<string>([optionId])
  const underscoreParts = optionId.split('_')
  const slashParts = optionId.split('/')

  if (underscoreParts.length > 1) {
    candidates.add(underscoreParts[underscoreParts.length - 1])
  }

  if (slashParts.length > 1) {
    candidates.add(slashParts[slashParts.length - 1])
  }

  return Array.from(candidates)
}

export async function fetchMatcherOption(optionId: string): Promise<MatcherOption | null> {
  const collectionsToTry = ['portfolioItems', 'flashes', 'healedPortfolioItems'] as const
  const candidates = getItemCandidates(optionId)

  for (const candidateId of candidates) {
    for (const collectionName of collectionsToTry) {
      const snapshot = await getDoc(doc(db, collectionName, candidateId))
      if (!snapshot.exists()) {
        continue
      }

      const data = snapshot.data() as {
        imageUrl?: string
        compressedImageUrl?: string
        caption?: string
        artistId?: string
      }

      const imageUrl = data.compressedImageUrl || data.imageUrl
      if (!imageUrl) {
        continue
      }

      return {
        id: optionId,
        imageUrl,
        caption: data.caption,
        artistId: data.artistId,
      }
    }
  }

  return null
}

export function getOrCreatePairwiseUserId(authUserId?: string): string {
  if (authUserId) {
    localStorage.setItem(PAIRWISE_USER_KEY, authUserId)
    return authUserId
  }

  const existing = localStorage.getItem(PAIRWISE_USER_KEY)
  if (existing) {
    return existing
  }

  const generated = `anon_${crypto.randomUUID()}`
  localStorage.setItem(PAIRWISE_USER_KEY, generated)
  return generated
}

export function getSavedPairwiseSessionId(): string | null {
  return localStorage.getItem(PAIRWISE_SESSION_KEY)
}

export function savePairwiseSessionId(sessionId: string): void {
  localStorage.setItem(PAIRWISE_SESSION_KEY, sessionId)
}

export function clearSavedPairwiseSessionId(): void {
  localStorage.removeItem(PAIRWISE_SESSION_KEY)
}
